import { useMemo, useCallback } from 'react';
import { useAuth } from '../context/auth';
import { 
  hasPermission, 
  hasRole, 
  canManageRole, 
  getAvailableRoles,
  ROLES,
  ROLE_LABELS,
  PERMISSIONS 
} from '../constants/roles';

export const useRoles = () => {
  const { userProfile } = useAuth();
  
  const userRole = useMemo(() => {
    return userProfile?.role || ROLES.USER;
  }, [userProfile]);

  const roleLabel = useMemo(() => {
    return ROLE_LABELS[userRole] || 'User';
  }, [userRole]);

  const checkPermission = useCallback((permission) => {
    return hasPermission(userRole, permission);
  }, [userRole]);

  const checkRole = useCallback((requiredRole) => {
    return hasRole(userRole, requiredRole);
  }, [userRole]);

  const canManageUserRole = useCallback((targetRole) => {
    return canManageRole(userRole, targetRole);
  }, [userRole]);

  const availableRoles = useMemo(() => {
    return getAvailableRoles(userRole);
  }, [userRole]);

  const isAdmin = useMemo(() => {
    return checkRole(ROLES.ADMIN);
  }, [checkRole]);

  const isSuperAdmin = useMemo(() => {
    return checkRole(ROLES.SUPER_ADMIN);
  }, [checkRole]);

  const canAccessAdminPanel = useMemo(() => {
    return checkPermission(PERMISSIONS.ACCESS_ADMIN_PANEL);
  }, [checkPermission]);

  const canManageUsers = useMemo(() => {
    return checkPermission(PERMISSIONS.VIEW_USERS);
  }, [checkPermission]);

  const canManageRoles = useMemo(() => {
    return checkPermission(PERMISSIONS.MANAGE_ROLES);
  }, [checkPermission]);

  const canViewAnalytics = useMemo(() => {
    return checkPermission(PERMISSIONS.VIEW_ANALYTICS);
  }, [checkPermission]);

  const canManageSettings = useMemo(() => {
    return checkPermission(PERMISSIONS.MANAGE_SETTINGS);
  }, [checkPermission]);

  return {
    // Current user role info
    userRole,
    roleLabel,
    isAdmin,
    isSuperAdmin,
    
    // Permission checks
    checkPermission,
    checkRole,
    canManageUserRole,
    
    // Available actions
    availableRoles,
    canAccessAdminPanel,
    canManageUsers,
    canManageRoles,
    canViewAnalytics,
    canManageSettings,
    
    // Constants for convenience
    ROLES,
    ROLE_LABELS,
    PERMISSIONS
  };
}; 